// src/core/data/redux/rolesPermissionsOverview.tsx
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";

/* ================= TYPES ================= */

export interface RoleOverview {
  id: number;
  name: string;
  permissions: string[];
}

export interface UserOverview {
  id: number;
  name: string;
  email: string;
  roles: string[];
  permissions: string[];
}

export interface RolesPermissionsData {
  roles: RoleOverview[];
  users: UserOverview[];
}

export interface RolesPermissionsState {
  loading: boolean;
  data: RolesPermissionsData | null;
  error: string | null;
}

/* ================= INITIAL STATE ================= */

const initialState: RolesPermissionsState = {
  loading: false,
  data: null,
  error: null,
};

const backendRoute = all_routes.backend;

/* ================= THUNK ================= */

export const fetchRolesPermissionsOverview = createAsyncThunk<
  RolesPermissionsData,
  void,
  { rejectValue: { message: string } }
>("rolesPermissions/fetchOverview", async (_, { rejectWithValue }) => {
  try {
    const result = await apiRequestCall(
      "post",
      backendRoute.rolesPermissionsOverview
    );

    if (result?.data?.code === 200) {
      return result.data.data as RolesPermissionsData;
    }

    return rejectWithValue({
      message: result?.data?.message || "Failed to load overview",
    });
  } catch (err: any) {
    return rejectWithValue({
      message: err?.message || "Network error",
    });
  }
});

/* ================= SLICE ================= */

const rolesPermissionsSlice = createSlice({
  name: "rolesPermissions",
  initialState,
  reducers: {
    clearRolesPermissions(state) {
      state.loading = false;
      state.data = null;
      state.error = null;
    },

    updateRolesPermissionsLocal(
      state,
      action: PayloadAction<Partial<RolesPermissionsData>>
    ) {
      if (!state.data) return;
      state.data = { ...state.data, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRolesPermissionsOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRolesPermissionsOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchRolesPermissionsOverview.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message ||
          action.error.message ||
          "Something went wrong";
      });
  },
});

export const { clearRolesPermissions, updateRolesPermissionsLocal } =
  rolesPermissionsSlice.actions;

export default rolesPermissionsSlice.reducer;

/* ================= SELECTORS ================= */

export const selectRolesPermissions = (state: {
  rolesPermissions: RolesPermissionsState;
}) => state.rolesPermissions.data;

export const selectRolesPermissionsLoading = (state: {
  rolesPermissions: RolesPermissionsState;
}) => state.rolesPermissions.loading;
